import { BankingInfoResponse } from '@/types/bankingInfo';
import {
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import {
  convertToNumber,
  formatCurrency,
  formatCurrencyWithIconInput,
} from '@/utils/currency';
import { REGEX } from '@/constants/regex';
import { SVTextInput } from '@/components/core';

type AdvisorDashboardInterestCalculatorProps = {
  bankingInfo: BankingInfoResponse;
};

export default function AdvisorDashboardInterestCalculator({
  bankingInfo,
}: AdvisorDashboardInterestCalculatorProps) {
  const formMethods = useForm({
    defaultValues: {
      amount: '150,000',
      numberOfYears: '1',
    },
    mode: 'onBlur',
  });
  const [amount, numberOfYears] = formMethods.watch([
    'amount',
    'numberOfYears',
  ]);

  const { skyview1Apy, usNationalAverageApy } =
    bankingInfo.referenceInterestRates;
  const skyviewEstEarnings =
    convertToNumber(amount) *
    (Number(skyview1Apy) / 100) *
    Number(numberOfYears);
  const checkingEstEarnings =
    convertToNumber(amount) *
    (Number(usNationalAverageApy) / 100) *
    Number(numberOfYears);
  const incrementalInterest =
    skyviewEstEarnings -
    convertToNumber(amount) -
    (checkingEstEarnings - convertToNumber(amount));

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Maximize Client Interest Calculator
      </Typography>
      <FormProvider {...formMethods}>
        <Grid spacing={2} container>
          <Grid size={{ xs: 12, md: 6 }}>
            <SVTextInput
              valueFormatter={formatCurrencyWithIconInput}
              name="amount"
              label="Amount"
              rules={{
                pattern: {
                  regex: REGEX.CURRENCY_VALUE,
                  readablePatternName: 'amount',
                },
              }}
              inputProps={{ startAdornment: <Typography>$</Typography> }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <SVTextInput
              name="numberOfYears"
              label="Number of Years"
              rules={{
                pattern: {
                  regex: REGEX.DECIMAL_NUMBER,
                  readablePatternName: 'number',
                },
              }}
            />
          </Grid>
        </Grid>
      </FormProvider>
      <Table aria-label="client interest calculator">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell align="right" sx={{ color: 'gray', fontWeight: 'bold' }}>
              APY
            </TableCell>
            <TableCell align="right" sx={{ color: 'gray', fontWeight: 'bold' }}>
              Est Earnings
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Interest Rate</TableCell>
            <TableCell align="right">{skyview1Apy}%</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {isNaN(skyviewEstEarnings)
                ? ''
                : formatCurrency({ amount: skyviewEstEarnings })}
            </TableCell>
          </TableRow>
          <TableRow sx={{ border: 0 }}>
            <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
              {'US Checking Nat.\nAvg. Interest Rate'}
            </TableCell>
            <TableCell align="right">{usNationalAverageApy}%</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              {isNaN(checkingEstEarnings)
                ? ''
                : formatCurrency({ amount: checkingEstEarnings })}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          mt: 2,
          pl: 2,
          pr: 2,
        }}>
        <Typography fontWeight="bold">Incremental Interest</Typography>
        <Typography fontWeight="bold">
          {isNaN(incrementalInterest)
            ? ''
            : formatCurrency({ amount: incrementalInterest })}
        </Typography>
      </Box>
    </Card>
  );
}
